import { reduxPersistor } from '@/lib';

import type { ITeamMembersRole, ITemMemberPermission, TargetEnvTypes } from '../types';

const TIME_FORMAT = 'hh:m a';
const DATE_FORMAT = 'MMM, dd yyyy';
const FULL_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss zzz';
const FULL_DATE_TIME_FORMAT_ISO_Z = "yyyy-MM-dd'T'HH:mm:ss.SSSX";
const FULL_DATE_TIME_FORMAT2 = 'MMMM d yyyy, h:mm aa';
const CALENDAR_DATE_TIME_FORMAT = 'MMMM d, yyyy h:mm aa';
export const MS_IN_SECOND = 1000;
export const MS_IN_TEN_SECONDS = MS_IN_SECOND * 10;
export const MS_IN_MINUTE = MS_IN_SECOND * 60;
export const MS_IN_HOUR = MS_IN_MINUTE * 60;
export const MS_IN_DAY = MS_IN_HOUR * 24;

export const dateConst = {
	TIME_FORMAT,
	DATE_FORMAT,
	FULL_DATE_TIME_FORMAT,
	FULL_DATE_TIME_FORMAT_ISO_Z,
	FULL_DATE_TIME_FORMAT2,
	CALENDAR_DATE_TIME_FORMAT,
};

export const LIVE_ENVIRONMENT = (import.meta.env.VITE_BREET_ENV || 'production') as TargetEnvTypes;

export const getTargetEnvironment = (): TargetEnvTypes =>
	reduxPersistor.getData('app')?.targetEnvironment === 'sandbox' ? 'sandbox' : LIVE_ENVIRONMENT;

export const baseApiHeaders = {
	get 'x-breet-env'() {
		return getTargetEnvironment();
	},
};

export const HIDE_TOAST_KEY = 'x-hide-toast';

export const KYBSTATUSES = {
	COMPLETED: 'completed',
	REJECTED: 'rejected',
	PARTIAL: 'partial',
	PENDING: 'pending',
} as const;

export const CURRENCYCONFIG = {
	NGN: { country: 'Nigeria', locale: 'en-NG', symbol: '₦', currency: 'nigerian naira', currencyShort: 'naira', code: 'NGN' },
	GHS: { country: 'Ghana', locale: 'en-GH', symbol: '₵', currency: 'ghanaian cedi', currencyShort: 'cedi', code: 'GHS' },
	USD: {
		country: 'United States of America',
		locale: 'en-US',
		symbol: '$',
		currency: 'us dollar',
		currencyShort: 'dollar',
		code: 'USD',
	},
} as const;

export const CURRENCIES: CURRENCYCODETYPE[] = Object.values(CURRENCYCONFIG).map((config) => config.code);

export type CURRENCYCONFIGTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG];

export type CURRENCYCODETYPE = keyof typeof CURRENCYCONFIG;

export type CURRENCYSYMBOLTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['symbol'];

export type CURRENCYNAMETYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['currency'];

export type COUNTRYTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['country'];

export type LOCALETYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['locale'];

export type CURRENCYSHORTTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['currencyShort'];

export const SORT_LATEST_TO_OLDEST = -1;
export const SORT_OLDEST_TO_LATEST = 1;

export const INACTIVITY_TOTAL_DURATION = 5 * 60 * 1000;
export const INACTIVITY_WARNING_OFFSET = 1 * 60 * 1000;

export const ISPRODUCTION = typeof window !== 'undefined' ? window.location.origin === 'https://partners.breet.io' : false;

export type MENUICONTYPE =
	| 'dashboard'
	| 'wallet'
	| 'addresses'
	| 'transaction'
	| 'withdrawals'
	| 'analytics'
	| 'developer'
	| 'settings'
	| 'logout';
export type MENUTTITLEYPE =
	| 'Dashboard'
	| 'Wallet'
	| 'Addresses'
	| 'Transactions'
	| 'Withdrawals'
	| 'Analytics'
	| 'Developer'
	| 'Settings'
	| 'Log Out'
	| 'Notifications';

export const POSTMAN_DOCUMENTATION = 'https://docs.breet.io/';

export const FXRATE = {
	DEFAULT: 'default',
	BUY: 'buy',
} as const;

export const FXCONVERSION = {
	NGNToGHS: 'NGNToGHS',
	GHSToNGN: 'GHSToNGN',
	SELLUSD: 'SellUSD',
	FIATToUSD: 'FiatToUSD',
} as const;

export const OTPLENGTH = 6;
export const PINLENGTH = 4;

export const TWOFAWARNINGTHRESHOLD = {
	NGN: 20000000,
	GHS: 100000,
	USD: 10000,
};

export const HTTPERROR = {
	EMAIL: 'please enter email otp',
	AUTHENTICATOR: 'please enter authenticator otp',
	OTPSENT: 'OTP authentication details sent to the associated email',
	RESETLINKEXPIRED: 'reset link is invalid or has expired. Please request a new one.',
};

export const HTTPSTATUSCODE = {
	TWOFA: 202,
	NEWDEVICE: 206,
	TRANSACTIONNOAUTH: 403,
	MULTIPLETEAMS: 300,
};

export const FIXEDCACHEKEYS = {
	SAVE_WALLET_ADDRESS: 'saveWalletAddress',
	LOGIN: 'login',
} as const;

export const REGISTERBUSINESSPATH = '/register-business';
export const ACCEPTINVITEPATH = '/accept-invite';
export const permissionOptions: Record<ITemMemberPermission, { permission: ITemMemberPermission; label: string }> = {
	export: {
		label: 'Export Data',
		permission: 'export',
	},
	generate_secret_prod: {
		label: 'Generate Production Secret',
		permission: 'generate_secret_prod',
	},
	generate_secret_sandbox: {
		label: 'Generate Sandbox Secret',
		permission: 'generate_secret_sandbox',
	},
	manage_team: {
		label: 'Manage Team',
		permission: 'manage_team',
	},
	read: {
		label: 'Read',
		permission: 'read',
	},
	settings: {
		label: 'Settings',
		permission: 'settings',
	},
	view_api_keys_prod: {
		label: 'View Production API Keys',
		permission: 'view_api_keys_prod',
	},
	view_api_keys_sandbox: {
		label: 'View Sandbox API Keys',
		permission: 'view_api_keys_sandbox',
	},
	withdraw: {
		label: 'Withdraw',
		permission: 'withdraw',
	},
};

export const TEAM_MEMBER_ROLE_AND_PERMISSIONS: Record<ITeamMembersRole, { permission: ITemMemberPermission; label: string }[]> = {
	customer_support: [permissionOptions.read],
	developer: [permissionOptions.read, permissionOptions.view_api_keys_sandbox],
	subadmin: [permissionOptions.read, permissionOptions.manage_team, permissionOptions.export, permissionOptions.settings],
	owner: [
		permissionOptions.read,
		permissionOptions.view_api_keys_sandbox,
		permissionOptions.view_api_keys_prod,
		permissionOptions.generate_secret_sandbox,
		permissionOptions.generate_secret_prod,
		permissionOptions.manage_team,
		permissionOptions.export,
		permissionOptions.settings,
	],
	super_admin: [
		permissionOptions.read,
		permissionOptions.view_api_keys_sandbox,
		permissionOptions.view_api_keys_prod,
		permissionOptions.generate_secret_sandbox,
		permissionOptions.generate_secret_prod,
		permissionOptions.manage_team,
		permissionOptions.export,
		permissionOptions.settings,
	],
};
